import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Clock, LayoutDashboard, CalendarRange, Library, Users, Bell, Settings, LogOut, Copy, CreditCard, ArrowLeft, CalendarDays } from "lucide-react";
import { ClockToggle } from "../../lib/clockFormat";
import { toast } from "sonner";
import api from "../../lib/api";
import { copyToClipboard } from "../../lib/clipboard";
import { useAuth } from "../../context/AuthContext";
import { clearToken } from "../../lib/authStorage";

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard" },
  { to: "/admin/schedule", icon: CalendarRange, label: "Schedule layout" },
  { to: "/admin/directory", icon: Library, label: "Directory" },
  { to: "/admin/pupils", icon: Users, label: "Pupils" },
  { to: "/admin/notices", icon: Bell, label: "Notices" },
  { to: "/admin/calendar", icon: CalendarDays, label: "Calendar" },
  { to: "/admin/billing", icon: CreditCard, label: "Billing" },
  { to: "/admin/settings", icon: Settings, label: "Settings" },
];

export default function AdminLayout() {
  const { user, logout, saveSession } = useAuth();
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    api.get("/school/billing/status").then((r) => setBilling(r.data)).catch(() => {});
  }, []);

  const copyCode = async () => {
    const ok = await copyToClipboard(user.join_code);
    ok ? toast.success("Join code copied!") : toast.error("Couldn't copy — the code is shown on the dashboard");
  };

  const exitImpersonation = () => {
    clearToken("school");
    window.location.href = "/super/schools";
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="admin-layout">
      {user.impersonated && (
        <div className="bg-[#D8B4FE] border-b-2 border-zinc-900 px-4 py-2 flex items-center justify-between" data-testid="impersonation-banner">
          <span className="font-bold text-sm">You are viewing as <span className="font-display font-black">{user.name}</span> (super-admin mode)</span>
          <button onClick={exitImpersonation} data-testid="exit-impersonation-btn" className="nb-btn bg-white px-3 py-1.5 text-xs">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to super admin
          </button>
        </div>
      )}
      {billing && !billing.fees_waived && billing.billing_ok && billing.subscription_status === "trialing" && billing.trial_days_left != null && (
        <div className="bg-[#FDE68A] border-b-2 border-zinc-900 px-4 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="trial-banner">
          <span className="font-bold text-sm">Free trial — {billing.trial_days_left} day{billing.trial_days_left === 1 ? "" : "s"} left. Pick a plan to keep timetables live after it ends.</span>
          <button onClick={() => navigate("/admin/billing")} data-testid="trial-goto-billing-btn" className="nb-btn bg-[#1E3A8A] text-white px-3 py-1.5 text-xs">
            <CreditCard className="w-3.5 h-3.5" /> Choose a plan
          </button>
        </div>
      )}
      {billing && !billing.billing_ok && (
        <div className="bg-red-100 border-b-2 border-zinc-900 px-4 py-2 flex items-center justify-between" data-testid="billing-warning-banner">
          <span className="font-bold text-sm text-red-800">Trial/subscription ended — admin features are paused.</span>
          <button onClick={() => navigate("/admin/billing")} data-testid="goto-billing-btn" className="nb-btn bg-[#1E3A8A] text-white px-3 py-1.5 text-xs">
            <CreditCard className="w-3.5 h-3.5" /> Subscribe now
          </button>
        </div>
      )}
      <div className="flex flex-1 min-h-0">
        <aside className="w-16 lg:w-64 shrink-0 border-r-2 border-zinc-900 bg-white flex flex-col">
          <div className="p-4 border-b-2 border-zinc-900 flex items-center gap-2">
            <div className="w-9 h-9 shrink-0 rounded-xl border-2 border-zinc-900 bg-[#A7E4D3] flex items-center justify-center shadow-[3px_3px_0px_0px_#18181B]">
              <Clock className="w-5 h-5" />
            </div>
            <div className="hidden lg:block min-w-0">
              <div className="font-display font-black text-lg leading-tight truncate" data-testid="admin-school-name">{user.name}</div>
              <div className="text-xs text-zinc-500">School admin</div>
            </div>
          </div>
          <nav className="flex-1 p-3 space-y-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-3 py-2.5 font-bold text-sm border-2 transition-colors duration-150 ${
                    isActive ? "bg-[#1E3A8A] text-white border-zinc-900 shadow-[3px_3px_0px_0px_#18181B]" : "border-transparent text-zinc-600 hover:bg-zinc-100"
                  }`
                }>
                <n.icon className="w-5 h-5 shrink-0" />
                <span className="hidden lg:inline">{n.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t-2 border-zinc-900 space-y-2">
            <button onClick={copyCode} data-testid="copy-join-code-btn"
              className="w-full flex items-center gap-2 rounded-xl border-2 border-zinc-900 bg-[#FDE68A] px-3 py-2 font-bold text-sm shadow-[3px_3px_0px_0px_#18181B] active:translate-x-[1px] active:translate-y-[1px] active:shadow-[1px_1px_0px_0px_#18181B] transition-[transform,box-shadow] duration-150">
              <Copy className="w-4 h-4 shrink-0" />
              <span className="hidden lg:inline">Code: {user.join_code}</span>
            </button>
            <ClockToggle className="nb-btn bg-white px-3 py-2 text-sm font-bold" />
            <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout-btn"
              className="w-full flex items-center gap-2 rounded-xl px-3 py-2 font-bold text-sm text-zinc-500 hover:bg-zinc-100 transition-colors duration-150">
              <LogOut className="w-4 h-4 shrink-0" />
              <span className="hidden lg:inline">Log out</span>
            </button>
          </div>
        </aside>
        <main className="flex-1 min-w-0 p-6 lg:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
